import type { RentalMapCluster } from './rental_map_clusters.js';
import type { RentalAgencyPin } from './rental_map_pins.js';

/** O texto do pino de agência e do agrupador (#3995). */
export type RentalMapPinCopy = {
  /** O menor total SEM centavos ("R$ 450") — a pílula e o nome. */
  priceLabel(amount: number): string;
  /** O nome acessível ("Localiza, a partir de R$ 450, 3 carros"); `price`
   *  ausente quando nenhuma oferta da agência tem total. */
  pinLabel(pin: { company: string; price?: string; count: number }): string;
  /** A contagem do agrupador na pílula ("3 agências"). */
  clusterCountLabel(count: number): string;
  /** O nome acessível do agrupador, que diz o gesto ("3 agências, a partir de
   *  R$ 180. Aproximar"). */
  clusterLabel(cluster: { count: number; price?: string }): string;
};

function pinPart(className: string): HTMLSpanElement {
  const part = document.createElement('span');
  part.className = className;
  return part;
}

function priceOf(fromPrice: number | undefined, copy: RentalMapPinCopy | undefined) {
  return copy && fromPrice !== undefined ? copy.priceLabel(fromPrice) : undefined;
}

/** A casca comum: o nó de tamanho zero na coordenada (botão ou só leitura), a
 *  pílula suspensa e o bico — a receita da Mapbox para marcador customizado,
 *  a mesma do hotel. */
function pinShell(
  key: string,
  name: string,
  pressable: boolean,
): { element: HTMLElement; body: HTMLSpanElement } {
  const element = document.createElement(pressable ? 'button' : 'div');
  element.className = 'iuk-rental-map-pin';
  element.dataset.pinKey = key;
  if (element instanceof HTMLButtonElement) element.type = 'button';
  else element.setAttribute('role', 'img');
  element.setAttribute('aria-label', name);
  const body = pinPart('iuk-rental-map-pin-body');
  element.append(body, pinPart('iuk-rental-map-pin-tail'));
  return { element, body };
}

/** A pílula: o destaque (preço, ou a contagem do agrupador) e o resto. */
function fillBody(body: HTMLSpanElement, lead: string | undefined, rest: string): void {
  if (lead !== undefined) {
    const amount = pinPart('iuk-rental-map-pin-price');
    amount.textContent = lead;
    body.append(amount, document.createTextNode(' · '));
  }
  const tail = pinPart('iuk-rental-map-pin-name');
  tail.textContent = rest;
  body.append(tail);
}

/**
 * O pino de uma AGÊNCIA: `R$ {menor} · {Locadora}` (o preço primeiro, como o
 * hotel mobile, #5125). Com `pressable` é BOTÃO — o toque filtra a lista pela
 * agência (#3995) —; sem ele, só leitura (`role="img"`).
 */
export function agencyPin(
  pin: RentalAgencyPin,
  copy: RentalMapPinCopy | undefined,
  pressable: boolean,
): HTMLElement {
  const price = priceOf(pin.fromPrice, copy);
  const name = copy
    ? copy.pinLabel({
        company: pin.company,
        ...(price === undefined ? {} : { price }),
        count: pin.offerIds.length,
      })
    : pin.company;
  const { element, body } = pinShell(pin.key, name, pressable);
  fillBody(body, price, pin.company);
  return element;
}

/**
 * O AGRUPADOR de pinos que colidem: `{n} agências · R$ {menor}+`. Sempre
 * botão — o toque aproxima até os pinos se separarem, com ou sem filtro.
 */
export function clusterPin(
  cluster: RentalMapCluster,
  copy: RentalMapPinCopy | undefined,
): HTMLElement {
  const count = cluster.pins.length;
  const price = priceOf(cluster.fromPrice, copy);
  const countLabel = copy ? copy.clusterCountLabel(count) : String(count);
  const name = copy
    ? copy.clusterLabel({ count, ...(price === undefined ? {} : { price }) })
    : countLabel;
  const { element, body } = pinShell(cluster.key, name, true);
  element.dataset.cluster = 'true';
  if (price === undefined) fillBody(body, undefined, countLabel);
  else fillBody(body, countLabel, `${price}+`);
  return element;
}
